import React, { useState, useEffect } from 'react';
import { Icon } from './Icon.js';
import { supabase } from '../supabase.js';
import { calculateLevelProgress } from '../utils/xpLevelUtils';
import PinnedMedals from './PinnedMedals.js';
import './ProfileCard.css';

function ProfileCard({ user, className = '', onProfileToggle, onProfilePictureUpdate }) {
  // Fallback values if user data is not available
  const displayName = user?.username || 'User';
  const location = user?.location || 'Location not set';
  const bio = user?.bio || 'No bio available';
  const followersCount = user?.followers_count || 0;
  const followingCount = user?.following_count || 0;
  const profileImage = user?.avatar_url || '/default-avatar.jpg';
  const followers = user?.followers || [];
  const following = user?.following || [];
  const level = user?.user_level ?? user?.level ?? 1;
  const xp = user?.xp ?? 0;
  
  // State for level tooltip
  const [levelProgress, setLevelProgress] = useState(null);
  const [showLevelTooltip, setShowLevelTooltip] = useState(false);
  
  // Load level progress data for tooltip
  useEffect(() => {
    const loadTooltipData = async () => {
      const progress = await calculateLevelProgress(xp, level);
      setLevelProgress(progress);
    };
    
    if (xp !== undefined && level !== undefined) {
      loadTooltipData();
    }
  }, [xp, level]);
  
  // State for profile visibility toggle
  const [publicProfile, setPublicProfile] = useState(user?.public_profile !== false);
  
  // State for showing followers/following lists
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  
  // State for profile picture editing
  const [showProfilePicEditor, setShowProfilePicEditor] = useState(false);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentProfileImage, setCurrentProfileImage] = useState(user?.avatar_url || user?.profile_picture_url || '/default-avatar.jpg');
  
  // State for bio editing
  const [showBioEditor, setShowBioEditor] = useState(false);
  const [editedBio, setEditedBio] = useState(bio);

  // State for checking if this is the user's own profile
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  // Update current profile image when user prop changes
  useEffect(() => {
    const newProfileImage = user?.avatar_url || user?.profile_picture_url || '/default-avatar.jpg';
    setCurrentProfileImage(newProfileImage);
  }, [user?.avatar_url, user?.profile_picture_url]);

  // Check if this is the user's own profile (use getSession to avoid duplicate GET /auth/v1/user)
  useEffect(() => {
    const checkOwnProfile = async () => {
      const userId = user?.user_id || user?.id || user?.uid;
      setProfileUserId(userId);
      
      if (userId) {
        try {
          const { data: { session } = {} } = await supabase.auth.getSession();
          const currentUserId = session?.user?.id;
          if (currentUserId) {
            setIsOwnProfile(currentUserId === userId);
          }
        } catch (error) {
          console.error('Error checking own profile:', error);
          setIsOwnProfile(false);
        }
      }
    };
    
    checkOwnProfile();
  }, [user?.user_id, user?.id, user?.uid]);

  // Handle profile toggle
  const handleProfileToggle = () => {
    const newPublicProfile = !publicProfile;
    setPublicProfile(newPublicProfile);
    
    // Call the parent component's callback if provided
    if (onProfileToggle) {
      onProfileToggle(newPublicProfile);
    }
  };

  // Handle followers click
  const handleFollowersClick = () => {
    setShowFollowers(!showFollowers);
    setShowFollowing(false); // Close following if open
  };

  // Handle following click
  const handleFollowingClick = () => {
    setShowFollowing(!showFollowing);
    setShowFollowers(false); // Close followers if open
  };

  // Function to delete old profile picture
  const deleteOldProfilePicture = async (userId) => {
    try {
      // Get all files in the user-profile-pictures bucket
      const { data: files, error: listError } = await supabase.storage
        .from('user-profile-pictures')
        .list('', {
          search: `profile-${userId}-`
        });

      if (listError) {
        console.error('Error listing files:', listError);
        return;
      }

      if (files && files.length > 0) {
        // Filter files that match the exact user ID pattern
        const userFiles = files.filter(file => 
          file.name.startsWith(`profile-${userId}-`)
        );
        
        if (userFiles.length > 0) {
          const filesToDelete = userFiles.map(file => file.name);
          
          const { error: deleteError } = await supabase.storage
            .from('user-profile-pictures')
            .remove(filesToDelete);

          if (deleteError) {
            console.error('Error deleting old profile pictures:', deleteError);
          } else {
            console.log('Deleted old profile pictures:', filesToDelete);
          }
        }
      }
    } catch (error) {
      console.error('Error in deleteOldProfilePicture:', error);
    }
  };

  // Handle profile picture change - only create preview, don't upload yet
  const handleProfilePicChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      // Create preview URL and store the file for later upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicPreview(e.target.result);
        // Store the file for upload when Save is clicked
        setSelectedFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle profile picture editor toggle
  const handleProfilePicEditorToggle = () => {
    setShowProfilePicEditor(!showProfilePicEditor);
  };

  // Handle profile picture save - now handles the actual upload
  const handleProfilePicSave = async () => {
    if (!selectedFile) {
      alert('Please select an image first');
      return;
    }

    try {
      const file = selectedFile;
      const fileExt = file.name.split('.').pop();
      
      // Debug: Log the user object to see what fields are available
      console.log('User object:', user);
      console.log('Available user fields:', Object.keys(user || {}));
      
      // Try multiple possible user ID field names
      let userId = user?.user_id || user?.id || user?.uid || user?.user?.id || user?.user?.user_id;
      
      // If still no user ID found, try to get it from Supabase auth (use getSession to avoid extra GET /auth/v1/user)
      if (!userId) {
        console.log('No user ID in props, trying to get from Supabase auth...');
        try {
          const { data: { session } = {} } = await supabase.auth.getSession();
          if (session?.user?.id) {
            userId = session.user.id;
            console.log('Got user ID from auth:', userId);
          }
        } catch (authErr) {
          console.error('Error getting auth user:', authErr);
        }
      }
      
      if (!userId) {
        console.error('No user ID found in user object:', user);
        alert('User ID not found. Available fields: ' + Object.keys(user || {}).join(', ') + '. Please log in again.');
        return;
      }
      
      console.log('Using user ID:', userId);
      
      // Delete old profile pictures first
      await deleteOldProfilePicture(userId);
      
      const fileName = `profile-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      
      // Upload with optimized caching (7 days = 604800 seconds)
      // Profile pictures may change more frequently than post images
      const { data, error } = await supabase.storage
        .from('user-profile-pictures')
        .upload(fileName, file, {
          cacheControl: '604800', // 7 days
          upsert: false
        });

      if (error) {
        console.error('Error uploading file:', error);
        alert('Failed to upload image. Please try again.');
        return;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('user-profile-pictures')
        .getPublicUrl(fileName);

      console.log('Profile picture uploaded successfully:', urlData.publicUrl);
      
      // Update the user's profile_picture_url in the database
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ 
          profile_picture_url: urlData.publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating profile picture URL:', updateError);
        alert('Image uploaded but failed to update profile. Please try again.');
        return;
      }

      console.log('Profile picture URL updated in database successfully');
      
      // Update the current profile image
      setCurrentProfileImage(urlData.publicUrl);
      
      // Notify parent component of the update
      if (onProfilePictureUpdate) {
        onProfilePictureUpdate(urlData.publicUrl);
      }
      
      console.log('Profile picture saved successfully');
      
      // Close the editor and clear the preview
      setShowProfilePicEditor(false);
      setProfilePicPreview(null);
      setSelectedFile(null);
      setUploadedImageUrl(null);
      
    } catch (error) {
      console.error('Error saving profile picture:', error);
      alert('Failed to save profile picture. Please try again.');
    }
  };

  // Handle profile picture remove
  const handleProfilePicRemove = async () => {
    try {
      const userId = user?.user_id || user?.id || user?.uid || user?.user?.id || user?.user?.user_id;
      
      if (!userId) {
        alert('User ID not found. Please log in again.');
        return;
      }

      // Delete all existing profile pictures from storage
      await deleteOldProfilePicture(userId);

      // Update database to remove profile picture URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ 
          profile_picture_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error removing profile picture:', updateError);
        alert('Failed to remove profile picture. Please try again.');
        return;
      }

      // Update the current profile image to default
      setCurrentProfileImage('/default-avatar.jpg');
      
      console.log('Profile picture removed successfully');
      
      // Close the modal
      setShowProfilePicEditor(false);
      setProfilePicPreview(null);
      setUploadedImageUrl(null);
      
      // Notify parent component
      if (onProfilePictureUpdate) {
        onProfilePictureUpdate(null);
      }
      
    } catch (error) {
      console.error('Error removing profile picture:', error);
      alert('Failed to remove profile picture. Please try again.');
    }
  };

  // Handle profile picture cancel
  const handleProfilePicCancel = () => {
    setShowProfilePicEditor(false);
    setProfilePicPreview(null);
    setSelectedFile(null);
    setUploadedImageUrl(null);
    // Don't update currentProfileImage - keep the existing one
  };

  // Handle bio editor toggle
  const handleBioEditorToggle = () => {
    setShowBioEditor(!showBioEditor);
    if (!showBioEditor) {
      setEditedBio(bio); // Reset to original bio when opening
    }
  };

  // Handle bio change
  const handleBioChange = (event) => {
    setEditedBio(event.target.value);
  };

  // Handle bio save
  const handleBioSave = () => {
    // Here you would save the bio to your server
    console.log('Saving bio:', editedBio);
    setShowBioEditor(false);
  };

  // Handle bio cancel
  const handleBioCancel = () => {
    setShowBioEditor(false);
    setEditedBio(bio); // Reset to original bio
  };

  return (
    <div className={`profile-card ${className}`}>
      {/* Portfolio Header Section with Gradient Background */}
      <div className="profile-card-header">
        <div className="profile-card-header-gradient"></div>
        
        {/* Pinned Medals - Top Right */}
        {profileUserId && (
          <div className="profile-card-header-medals">
            <PinnedMedals userId={profileUserId} isOwnProfile={isOwnProfile} />
          </div>
        )}
        
        {/* Main Content Container */}
        <div className="profile-card-header-content">
          {/* Profile Picture */}
          <div className="profile-card-main-photo profile-card-photo-hover-container">
            <div className="profile-card-avatar">
              {currentProfileImage && currentProfileImage !== '/default-avatar.jpg' ? (
                <img 
                  src={currentProfileImage} 
                  alt={`${displayName}'s profile`}
                  className="profile-card-avatar-img"
                />
              ) : (
                <div className="profile-card-avatar-fallback">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              
              {/* Profile Picture Change Button */}
              <button 
                className="profile-card-change-photo-btn"
                onClick={handleProfilePicEditorToggle}
                title="Change profile picture"
              >
                <Icon name="Camera" className="profile-card-change-photo-icon" />
              </button>
            </div>
          </div>

          {/* Profile Info Section */}
          <div className="profile-card-main-info">
            <div className="profile-card-name-section">
              <h1 className="profile-card-name">
                {displayName}
                <div 
                  className="profile-card-level-badge"
                  onMouseEnter={() => setShowLevelTooltip(true)}
                  onMouseLeave={() => setShowLevelTooltip(false)}
                  style={{ position: 'relative', cursor: 'help' }}
                >
                  {level}
                  {showLevelTooltip && levelProgress && (
                    <div className="profile-card-level-tooltip">
                      <div className="tooltip-header">
                        <span className="tooltip-level">Level {levelProgress.currentLevel}</span>
                        {levelProgress.nextLevel && (
                          <span className="tooltip-xp">{xp} / {levelProgress.nextLevelXP} XP</span>
                        )}
                      </div>
                      {levelProgress.nextLevel ? (
                        <>
                          <div className="tooltip-progress-bar">
                            <div 
                              className="tooltip-progress-fill"
                              style={{ width: `${levelProgress.progress * 100}%` }}
                            />
                          </div>
                          <div className="tooltip-footer">
                            {levelProgress.xpNeeded} XP to Level {levelProgress.nextLevel}
                          </div>
                        </>
                      ) : (
                        <div className="tooltip-footer">Maximum level reached!</div>
                      )}
                    </div>
                  )}
                </div>
              </h1>
              <div className="profile-card-pro-badge">PRO</div>
            </div>
            <p className="profile-card-title">{bio || 'Interface and Brand Designer'}</p>
            <p className="profile-card-location">{location}</p>
            
            {/* Action Buttons */}
            <div className="profile-card-actions">
              <button className="profile-card-follow-btn">Follow</button>
              <button className="profile-card-contact-btn">Get in touch</button>
            </div>
          </div>

          {/* Right Side Section with Badges and Stats */}
          <div className="profile-card-right-section">
            {/* Stats Section */}
            <div className="profile-card-stats-vertical">
              <div className="profile-card-stat-item" onClick={handleFollowersClick} style={{ cursor: 'pointer' }}>
                <div className="profile-card-stat-number">{followersCount}</div>
                <div className="profile-card-stat-label">Followers</div>
              </div>
              <div className="profile-card-stat-item" onClick={handleFollowingClick} style={{ cursor: 'pointer' }}>
                <div className="profile-card-stat-number">{followingCount}</div>
                <div className="profile-card-stat-label">Following</div>
              </div>
              <div className="profile-card-stat-item">
                <div className="profile-card-stat-number">548</div>
                <div className="profile-card-stat-label">Likes</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Navigation Tabs */}
      <div className="profile-card-tabs">
        <button className="profile-card-tab active">
          Activity <span className="profile-card-tab-count">28</span>
        </button>
        <button className="profile-card-tab">
          Protocols <span className="profile-card-tab-count">{user?.protocols_count || 0}</span>
        </button>
        <button className="profile-card-tab">
          Posts <span className="profile-card-tab-count">{user?.posts_count || 0}</span>
        </button>
      </div>


      {/* Followers List Modal */}
      {showFollowers && (
        <div className="profile-card-modal">
          <div className="profile-card-modal-content">
            <div className="profile-card-modal-header">
              <h3>Followers ({followersCount})</h3>
              <button 
                className="profile-card-modal-close"
                onClick={() => setShowFollowers(false)}
              >
                ×
              </button>
            </div>
            <div className="profile-card-modal-body">
              {followers.length > 0 ? (
                <div className="profile-card-user-list">
                  {followers.map((follower, index) => (
                    <div key={follower.id || index} className="profile-card-user-item">
                      <div className="profile-card-user-avatar">
                        {follower.profile_picture_url ? (
                          <img 
                            src={follower.profile_picture_url} 
                            alt={follower.username || follower.display_name || 'User'}
                            className="profile-card-user-avatar-img"
                          />
                        ) : (
                          <span className="profile-card-user-avatar-fallback">
                            {(follower.username || follower.display_name || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="profile-card-user-info">
                        <div className="profile-card-user-name">
                          {follower.username || 'User'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="profile-card-empty-state">
                  No followers yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Following List Modal */}
      {showFollowing && (
        <div className="profile-card-modal">
          <div className="profile-card-modal-content">
            <div className="profile-card-modal-header">
              <h3>Following ({followingCount})</h3>
              <button 
                className="profile-card-modal-close"
                onClick={() => setShowFollowing(false)}
              >
                ×
              </button>
            </div>
            <div className="profile-card-modal-body">
              {following.length > 0 ? (
                <div className="profile-card-user-list">
                  {following.map((followed, index) => (
                    <div key={followed.id || index} className="profile-card-user-item">
                      <div className="profile-card-user-avatar">
                        {followed.profile_picture_url ? (
                          <img 
                            src={followed.profile_picture_url} 
                            alt={followed.username || followed.display_name || 'User'}
                            className="profile-card-user-avatar-img"
                          />
                        ) : (
                          <span className="profile-card-user-avatar-fallback">
                            {(followed.username || followed.display_name || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="profile-card-user-info">
                        <div className="profile-card-user-name">
                          {followed.username || 'User'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="profile-card-empty-state">
                  Not following anyone yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Picture Editor Modal */}
      {showProfilePicEditor && (
        <div className="profile-card-modal">
          <div className="profile-card-modal-content profile-pic-modal-simple">
            <div className="profile-card-modal-header">
              <h3>Profile Picture</h3>
              <button 
                className="profile-card-modal-close"
                onClick={handleProfilePicCancel}
              >
                ×
              </button>
            </div>
            <div className="profile-card-modal-body">
              <div className="profile-pic-simple-editor">
                <div className="profile-pic-current">
                  {profilePicPreview ? (
                    <img 
                      src={profilePicPreview} 
                      alt="New profile preview"
                      className="profile-pic-current-img"
                    />
                  ) : currentProfileImage && currentProfileImage !== '/default-avatar.jpg' ? (
                    <img 
                      src={currentProfileImage} 
                      alt="Current profile"
                      className="profile-pic-current-img"
                    />
                  ) : (
                    <div className="profile-pic-current-fallback">
                      <Icon name="User" className="profile-pic-current-icon" />
                    </div>
                  )}
                </div>
                
                <div className="profile-pic-simple-actions">
                  <label className="profile-pic-upload-btn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicChange}
                      style={{ display: 'none' }}
                    />
                    <Icon name="Upload" className="profile-pic-upload-icon" />
                    Upload
                  </label>
                  
                  <button 
                    className="profile-pic-remove-btn"
                    onClick={handleProfilePicRemove}
                    disabled={!currentProfileImage || currentProfileImage === '/default-avatar.jpg'}
                  >
                    <Icon name="Trash" className="profile-pic-remove-icon" />
                    Remove
                  </button>
                  
                  <button 
                    className="profile-pic-save-btn"
                    onClick={handleProfilePicSave}
                    disabled={!selectedFile}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Public Profile Toggle - Icon Only */}
      <div 
        className={`profile-card-toggle ${publicProfile ? 'public' : 'private'}`}
        title={publicProfile ? 'Public Profile - Click to make private' : 'Private Profile - Click to make public'}
        onClick={handleProfileToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleProfileToggle();
          }
        }}
      >
        <Icon 
          name={publicProfile ? 'Unlock' : 'Lock'} 
          className="profile-card-toggle-icon" 
        />
      </div>
    </div>
  );
}

export default ProfileCard;
